import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryParams } from '../hooks/misc';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useWorkspace } from './WorkspaceProvider';
import CustomSpinner from '../components/CustomSpinner';
import { sortItemsByDateModified } from '../utilities';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCurrentUser } from './UserProvider';

const templateQuery = loader('./TemplateProvider.graphql');
const paginatedTemplateQuery = loader('./TemplateProvider.paginated.graphql');

const TemplateProviderContext = createContext();
export const TemplateProvider = ({ children }) => {
  const { templateProviderFetchOptions } = useFlags();
  const DEBOUCE_MS = templateProviderFetchOptions?.debounce ?? 50;
  const PAGE_SIZE = templateProviderFetchOptions?.pageSize ?? 5;
  const client = useApolloClient();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const { workspaceId } = useWorkspace();
  const { getParam } = useQueryParams();
  const type = getParam('type');
  const online = useOnlineStatus();
  const { syncing } = useCurrentUser();

  const { data: { inspectionTemplates = [] } = {} } = useQuery(templateQuery, {
    fetchPolicy: 'cache-only',
  });

  async function fetchData() {
    while (true && online) {
      setLoading(true);
      const existingData = client.readQuery({ query: templateQuery }) ?? {
        inspectionTemplates: [],
      };

      const sortedTemplates = sortItemsByDateModified(
        [...existingData?.inspectionTemplates] || [],
      );

      const minDate = sortedTemplates.length
        ? new Date(
            parseInt(
              sortedTemplates[sortedTemplates.length - 1]?.dateModified,
              10,
            ),
          )
        : new Date(0);

      let data;
      try {
        ({ data } = await fetchTemplates({
          variables: {
            minDate,
            pageSize: PAGE_SIZE,
          },
        }));
      } catch (_) {
        setLoading(false);
        break;
      }

      if (!data) {
        setLoading(false);
        break;
      }

      const startData = existingData ?? { inspectionTemplates: [] };

      const newTemplateIds = data.inspectionTemplates.map((t) => t.id);

      const oldTemplates = startData.inspectionTemplates.filter(
        (t) => !newTemplateIds.includes(t.id),
      );

      const newTemplates = sortItemsByDateModified([
        ...oldTemplates,
        ...data.inspectionTemplates,
      ]);

      client.writeQuery({
        query: templateQuery,
        data: { inspectionTemplates: newTemplates },
      });

      setProgress(
        Math.round((newTemplates.length / data.inspectionTemplatesCount) * 100),
      );

      if (data?.inspectionTemplates?.length !== PAGE_SIZE) {
        setProgress(100);
        setLoading(false);
        break;
      }

      await new Promise((r) => setTimeout(r, DEBOUCE_MS));
    }
  }

  const refreshingRef = useRef(false);

  const refetchAllTemplates = async () => {
    if (!online || refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      setLoading(true);
      client.cache.evict({ fieldName: 'inspectionTemplates' });
      client.cache.gc();
      await fetchData();
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  };

  const [fetchTemplates] = useLazyQuery(paginatedTemplateQuery, {
    skip: !online || syncing,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTemplateTypes = useMemo(() => {
    const activeTypesSet = new Set(
      inspectionTemplates
        ?.filter((template) => !template.internalType && !template.isArchived)
        ?.map((template) => template.templateType) || [],
    );
    return Array.from(activeTypesSet) || [];
  }, [inspectionTemplates]);

  const templates = useMemo(() => {
    if (!!type) {
      return inspectionTemplates
        ?.filter((f) => !f.internalType)
        .filter((t) => t.templateType === type)
        .filter((a) => !a.isArchived)
        .filter((t) => t.workspace?.id === workspaceId || !t.workspace?.id);
    }
    return inspectionTemplates;
  }, [inspectionTemplates, type, workspaceId]);

  const activeTemplates = useMemo(
    () =>
      inspectionTemplates
        ?.filter((f) => !f.internalType)
        .filter((a) => !a.isArchived)
        .filter((t) => t.workspace?.id === workspaceId || !t.workspace?.id),
    [inspectionTemplates, workspaceId],
  );

  return (
    <TemplateProviderContext.Provider
      value={{
        templates,
        activeTemplates,
        allTemplates: inspectionTemplates,
        activeTemplateTypes,
        fetchTemplates,
        refetchAllTemplates,
      }}
    >
      {online && loading && !syncing && (
        <CustomSpinner text={`Fetching templates.. ${progress}%`} />
      )}
      {children}
    </TemplateProviderContext.Provider>
  );
};
export const useTemplates = () => useContext(TemplateProviderContext);
